import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiRequest } from '../../config/api';
import useAuth from '../../hooks/useAuth';
import './style.css';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSync, faSearch } from '@fortawesome/free-solid-svg-icons';

const Leadconnector = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get('code') || '';
  const { user, signed, loading } = useAuth();
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState(t('leadconnector.status.connecting'));
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState('');
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [ghlRow, setGhlRow] = useState(null);
  const [connected, setConnected] = useState(false);

  // 1) Se existir code na URL, troca por tokens e envia para o webhook informado
  useEffect(() => {
    (async () => {
      setError('');
      try {
        // Garante que o usuário está autenticado
        if (loading) return; // aguarda estado de auth
        if (!signed || !user?.id) {
          setStatus(t('leadconnector.status.needLogin'));
          return;
        }

        if (code) {
          setStatus(t('leadconnector.status.connecting'));
          // Troca o code por tokens (x-www-form-urlencoded)
          const formData = new URLSearchParams();
          formData.append('client_id', '66918be52e5e272f4e962463-m1l81eg5');
          formData.append('client_secret', '294f957a-02e8-42a2-9fb3-9bdc0a13b6cc');
          formData.append('grant_type', 'authorization_code');
          formData.append('user_type', 'Location');
          formData.append('code', code);

          const tokenResp = await fetch('https://services.leadconnectorhq.com/oauth/token', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formData.toString(),
          });

          if (!tokenResp.ok) {
            const text = await tokenResp.text();
            throw new Error(`Falha ao trocar code por tokens: ${tokenResp.status} ${text}`);
          }

          const tokenData = await tokenResp.json();

          // Envia TODOS os dados da resposta para o webhook informado
          await fetch('https://automacao.wappfy.com.br/webhook/ghl/token', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ ...tokenData, user_id: user.id }),
          });

          // Prossegue para carregar pipelines após webhook (persistência costuma ocorrer no backend)
          setStatus(t('leadconnector.status.loadingPipelines'));
        }

        // Busca a integração mais recente do usuário via API
        const instanceId = user.id; // Usando user.id como instanceId
        const integration = await apiRequest(`/integrations/highlevel/${instanceId}`);

        setGhlRow(integration || null);

        if (!integration?.access_token || !integration?.location_id) {
          setStatus(t('leadconnector.status.awaitAuth'));
          return;
        }

        setStatus(t('leadconnector.status.loadingPipelines'));
        setLoadingPipelines(true);
        const response = await fetch(`https://services.leadconnectorhq.com/opportunities/pipelines?locationId=${integration.location_id}`, {
          headers: {
            'Authorization': `Bearer ${integration.access_token}`,
            'Content-Type': 'application/json',
            'Version': '2021-07-28'
          }
        });

        if (!response.ok) {
          throw new Error(`Erro ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        const list = result?.pipelines || [];
        setPipelines(list);
        setStatus(t('leadconnector.status.selectPipeline'));
        setConnected(true);
        if (integration?.pipeline_id) setSelectedPipeline(integration.pipeline_id);
      } catch (err) {
        console.error('Leadconnector error:', err);
        setError(t('leadconnector.errors.loadData'));
      } finally {
        setLoadingPipelines(false);
      }
    })();
  }, [code, signed, loading, user?.id, t]);

  const handleSave = async () => {
    if (!ghlRow) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await apiRequest(`/integrations/highlevel/${ghlRow.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ pipeline_id: selectedPipeline })
      });
      setSuccess(i18n.language === 'pt' ? 'Pipeline Definida com Sucesso!' : 'Pipeline Defined Successfully!');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(t('leadconnector.errors.save'));
    }
    setSaving(false);
  };

  const handleRefreshPipelines = async () => {
    if (!ghlRow?.access_token) return;
    setLoadingPipelines(true);
    try {
      const response = await fetch(`https://services.leadconnectorhq.com/opportunities/pipelines?locationId=${ghlRow.location_id}`, {
        headers: {
          'Authorization': `Bearer ${ghlRow.access_token}`,
          'Content-Type': 'application/json',
          'Version': '2021-07-28'
        }
      });
      const result = await response.json();
      setPipelines(result?.pipelines || []);
    } catch (err) {
      console.error('Erro ao atualizar pipelines:', err);
    } finally {
      setLoadingPipelines(false);
    }
  };

  return (
    <div className="leadconnector-page">
      <div className="leadconnector-main">
        {/* Header: Logos e Indicador de Conexão */}
        <div className="header-row">
          {/* Logo Wappfy */}
          <div className="logo-section">
            <img
              src="https://pluugpwnzurerieiumxj.supabase.co/storage/v1/object/public/wappify/Logos/wappfy-w-logo.png"
              alt="Wappfy W"
              className="logo-w"
            />
            <img
              src="https://pluugpwnzurerieiumxj.supabase.co/storage/v1/object/public/wappify/Logos/wappfy-green.png"
              alt="Wappfy"
              className="logo-text"
            />
          </div>

          {/* Indicador de Conexão */}
          <div className="connection-section">
            <div className={`line-divider ${connected ? 'connected' : 'connecting'}`}></div>
            <div className="icon-container">
              <FontAwesomeIcon
                icon={connected ? faCheck : faSync}
                className={`status-icon ${connected ? 'connected' : 'connecting'}`}
                spin={!connected}
              />
            </div>
            <div className={`line-divider ${connected ? 'connected' : 'connecting'}`}></div>
          </div>

          {/* Logo HighLevel */}
          <div className="ghl-section">
            <img
              src="https://pluugpwnzurerieiumxj.supabase.co/storage/v1/object/public/wappify/Logos/svg/ghl_logo.svg"
              alt="HighLevel"
              className="ghl-logo"
            />
          </div>
        </div>

        {/* Banner de Status */}
        <div className={`status-container ${connected ? 'connected' : 'connecting'}`}>
          <h1 className="status-title">
            {connected
              ? (i18n.language === 'pt' ? 'Conexão Com o HighLevel Concluída!' : 'Connection to HighLevel Complete!')
              : (i18n.language === 'pt' ? 'Finalizando Conexão com a GoHighLevel...' : 'Finishing Connection to GoHighLevel...')
            }
          </h1>
        </div>

        {/* Conteúdo Principal */}
        {connected && (
          <div className="main-content">
            {pipelines.length > 0 && (
              <div className="instruction-text">
                <p>
                  <span className="text-regular">
                    {i18n.language === 'pt'
                      ? 'Crie Cards de CRM automaticamente quando receber mensagens. '
                      : 'Create CRM Cards automatically when receiving messages. '}
                  </span>
                  <span className="text-bold">
                    {i18n.language === 'pt'
                      ? 'Configure uma pipeline na aba abaixo '
                      : 'Configure a pipeline in the dropdown below '}
                  </span>
                  <span className="text-regular">
                    {i18n.language === 'pt'
                      ? 'e em seguida clique no botão atualizar.'
                      : 'and then click the update button.'}
                  </span>
                </p>
              </div>
            )}

            {/* Controles */}
            <div className="controls-container">
              {/* Botão Refresh */}
              <button
                className="btn-refresh"
                onClick={handleRefreshPipelines}
                disabled={loadingPipelines}
                style={{ marginRight: pipelines.length === 0 ? '32px' : '0' }}
              >
                <FontAwesomeIcon icon={faSearch} />
              </button>

              {/* Dropdown de Pipeline */}
              {pipelines.length > 0 && (
                <select
                  className="pipeline-dropdown"
                  value={selectedPipeline}
                  onChange={async (e) => {
                    setSelectedPipeline(e.target.value);
                    // Auto-save quando selecionar
                    if (e.target.value && ghlRow) {
                      setSaving(true);
                      try {
                        await apiRequest(`/integrations/highlevel/${ghlRow.id}`, {
                          method: 'PATCH',
                          body: JSON.stringify({ pipeline_id: e.target.value })
                        });
                        setSuccess(i18n.language === 'pt' ? 'Pipeline Definida com Sucesso!' : 'Pipeline Defined Successfully!');
                        setTimeout(() => setSuccess(''), 4000);
                      } catch (err) {
                        setError(t('leadconnector.errors.save'));
                      }
                      setSaving(false);
                    }
                  }}
                  disabled={saving}
                >
                  <option value="">
                    {i18n.language === 'pt' ? 'Selecionar Pipeline' : 'Select Pipeline'}
                  </option>
                  {pipelines.map((pipeline) => (
                    <option key={pipeline.id} value={pipeline.id}>
                      {pipeline.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Mensagens */}
            {success && <div className="msg-success">{success}</div>}
            {error && <div className="msg-error">{error}</div>}
          </div>
        )}

        {/* Botão Voltar para Home */}
        <div className="footer-actions">
          <button
            className="btn-home"
            onClick={() => navigate('/home')}
          >
            {t('leadconnector.back')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Leadconnector;


